
import React, { useEffect, useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, Button, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, BarChart, Bar } from 'recharts';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { firestore } from '../firebase.config';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];


const DashboardPage = () => {
  const theme = useTheme();
  const [expenses, setExpenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [openExpensesDialog, setOpenExpensesDialog] = useState(false);
  const [openTransactionsDialog, setOpenTransactionsDialog] = useState(false);

  // Real-time Firestore listeners
  useEffect(() => {
    // Use correct collection names for dynamic data
    const unsubExpenses = onSnapshot(query(collection(firestore, 'expense')), (snap) => {
      setExpenses(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const unsubInvoices = onSnapshot(query(collection(firestore, 'Invoices')), (snap) => {
      setInvoices(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const unsubQuotations = onSnapshot(query(collection(firestore, 'Quotations')), (snap) => {
      setQuotations(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const unsubTransactions = onSnapshot(query(collection(firestore, 'Accounting Transactions Master')), (snap) => {
      setTransactions(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => {
      unsubExpenses();
      unsubInvoices();
      unsubQuotations();
      unsubTransactions();
    };
  }, []);

  // Card totals
  const totalExpensesAmount = expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
  const totalTransactionsCount = transactions.length;
  const totalTransactionsAmount = transactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

  // Pie chart: Spend by category (Expenses)
  const pieData = Object.values(
    expenses.reduce((acc, e) => {
      const cat = e.category || 'Other';
      acc[cat] = acc[cat] || { name: cat, value: 0 };
      acc[cat].value += Number(e.amount) || 0;
      return acc;
    }, {})
  );

  // Line chart: Expense vs Income (Invoices as Income)
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const lineData = months.map((month, idx) => {
    const expense = expenses.filter(e => new Date(e.createdDate || e.date || '').getMonth() === idx)
      .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    const income = invoices.filter(i => new Date(i.date || '').getMonth() === idx)
      .reduce((sum, i) => sum + (Number(i.totalAmount) || 0), 0);
    return { month, Expense: expense, Income: income };
  });

  // Bar chart: Monthly transactions (count)
  const barData = months.map((month, idx) => {
    const total = transactions.filter(t => new Date(t.date || t.createdDate || '').getMonth() === idx).length;
    return { month, Transactions: total };
  });

  return (
    <Box sx={{ bgcolor: '#f3f6ff', minHeight: '100vh', py: { xs: 2, md: 4 }, px: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
     

        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
              Financial Dashboard
            </Typography>

            <Grid container spacing={3} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: 1,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'common.white',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                      Expenses
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      ₹{totalExpensesAmount.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {expenses.length} Entries
                    </Typography>
                    <Button variant="outlined" size="small" onClick={() => setOpenExpensesDialog(true)}>VIEW</Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    borderRadius: 3,
                    boxShadow: 1,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'common.white',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
                      Transactions
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {totalTransactionsCount.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      ₹{totalTransactionsAmount.toLocaleString()}
                    </Typography>
                    <Button variant="outlined" size="small" onClick={() => setOpenTransactionsDialog(true)}>VIEW</Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    height: 320,
                    borderRadius: 3,
                    boxShadow: 1,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'common.white',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      Spend by Category
                    </Typography>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={70}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Legend />
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    height: 320,
                    borderRadius: 3,
                    boxShadow: 1,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'common.white',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      Expense vs Income
                    </Typography>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={lineData}>
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line type="monotone" dataKey="Expense" stroke="#e57373" />
                        <Line type="monotone" dataKey="Income" stroke="#1976d2" />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card
                  sx={{
                    height: 320,
                    borderRadius: 3,
                    boxShadow: 1,
                    bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'common.white',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      Monthly Transactions
                    </Typography>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={barData}>
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="Transactions" fill="#1976d2" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box
          sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: 'text.secondary',
            fontSize: 12,
          }}
        >
          <Typography variant="caption">
            &copy; 2024 Company Name
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography variant="caption">Terms</Typography>
            <Typography variant="caption">Privacy</Typography>
          </Box>
        </Box>
      </Box>
      <Dialog open={openExpensesDialog} onClose={() => setOpenExpensesDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Recent Expenses
          <Typography variant="subtitle2" color="text.secondary">
            ₹{totalExpensesAmount.toLocaleString()} · {expenses.length} entries
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <List dense>
            {expenses.slice(0, 15).map((e) => {
              const amount = Number(e.amount) || 0;
              const date = e.createdDate || e.date || '';
              const category = e.category || 'Uncategorized';
              const description = e.description || e.remark || e.notes || '';
              const vendor = e.vendor || e.party || '';
              const secondaryParts = [
                category,
                vendor && `Vendor: ${vendor}`,
                description,
                date && new Date(date).toLocaleDateString(),
              ].filter(Boolean);
              return (
                <ListItem key={e.id} divider alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: 600 }}>
                        ₹{amount.toLocaleString()}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {secondaryParts.join(' · ')}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
            {expenses.length === 0 && (
              <Typography variant="body2" color="text.secondary">No expenses found.</Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExpensesDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openTransactionsDialog} onClose={() => setOpenTransactionsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Recent Transactions
          <Typography variant="subtitle2" color="text.secondary">
            {totalTransactionsCount} transactions · ₹{totalTransactionsAmount.toLocaleString()}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <List dense>
            {transactions.slice(0, 15).map((t) => {
              const amount = Number(t.amount) || 0;
              const date = t.date || t.createdDate || '';
              const type = t.type || t.transactionType || '';
              const ref = t.reference || t.refNo || '';
              const secondaryParts = [
                type,
                ref && `Ref: ${ref}`,
                date && new Date(date).toLocaleDateString(),
              ].filter(Boolean);
              return (
                <ListItem key={t.id} divider alignItems="flex-start">
                  <ListItemText
                    primary={
                      <Typography sx={{ fontWeight: 600 }}>
                        ₹{amount.toLocaleString()}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {secondaryParts.join(' · ')}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
            {transactions.length === 0 && (
              <Typography variant="body2" color="text.secondary">No transactions found.</Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTransactionsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardPage;
